import React, { createContext, useContext, useCallback, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// App-wide replacement for window.confirm / window.prompt with shadcn modals.
//
//   const { confirm, prompt } = useConfirm();
//   if (!(await confirm({ title, description, destructive: true }))) return;
//   const pick = await prompt({ title, label, defaultValue, options });
//
// One <ConfirmProvider> mounted at the app root renders a single AlertDialog
// (confirm) and a single Dialog (prompt); each call resolves a Promise.
const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null); // { title, description, confirmText, cancelText, destructive }
  const [promptState, setPromptState] = useState(null);   // { title, label, options, value }
  const resolver = useRef(null);

  const settle = (val) => { const r = resolver.current; resolver.current = null; setConfirmState(null); setPromptState(null); r && r(val); };

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setConfirmState({
      title: opts.title || "Are you sure?",
      description: opts.description || "",
      confirmText: opts.confirmText || (opts.destructive ? "Delete" : "Confirm"),
      cancelText: opts.cancelText || "Cancel",
      destructive: opts.destructive ?? true,
    });
  }), []);

  const prompt = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setPromptState({
      title: opts.title || "Enter a value",
      label: opts.label || "",
      description: opts.description || "",
      options: opts.options || null,          // [{value,label}] | string[] → render a Select
      confirmText: opts.confirmText || "OK",
      value: opts.defaultValue ?? (opts.options ? (opts.options[0]?.value ?? opts.options[0]) : ""),
    });
  }), []);

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}

      {/* CONFIRM (AlertDialog) */}
      <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) settle(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            {confirmState?.description ? (
              <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>{confirmState?.cancelText}</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                onClick={() => settle(true)}
                className={confirmState?.destructive ? "bg-red-600 hover:bg-red-700 text-white" : undefined}
              >
                {confirmState?.confirmText}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PROMPT (Dialog) */}
      <Dialog open={!!promptState} onOpenChange={(o) => { if (!o) settle(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{promptState?.title}</DialogTitle>
            {promptState?.description ? (
              <DialogDescription>{promptState.description}</DialogDescription>
            ) : null}
          </DialogHeader>
          {promptState && (
            promptState.options ? (
              <Select value={String(promptState.value ?? "")} onValueChange={(v) => setPromptState((s) => ({ ...s, value: v }))}>
                <SelectTrigger><SelectValue placeholder={promptState.label || "Select…"} /></SelectTrigger>
                <SelectContent>
                  {promptState.options.map((o) => {
                    const val = typeof o === "string" ? o : o.value;
                    const lbl = typeof o === "string" ? o : (o.label ?? o.value);
                    return <SelectItem key={val} value={String(val)}>{lbl}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Input
                autoFocus
                value={promptState.value ?? ""}
                placeholder={promptState.label || ""}
                onChange={(e) => setPromptState((s) => ({ ...s, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") settle(promptState.value); }}
              />
            )
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => settle(null)}>Cancel</Button>
            <Button onClick={() => settle(promptState?.value)}>{promptState?.confirmText}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
